import pluralize from 'pluralize';
import { memo, useMemo } from 'react';

interface IPluralizedProps {
  /**
   * The number of items
   * @type {number}
   * @memberof IPluralizedProps
   * @see https://www.npmjs.com/package/pluralize#usage
   * @example <Pluralized count={3}>duck</Pluralized> // ducks
   * @example <Pluralized count={1}>duck</Pluralized> // duck
   * @example <Pluralized>goose</Pluralized> // geese
   */
  count?: number;
  /**
   * The word to pluralize
   * @type {string}
   * @memberof IPluralizedProps
   * @see https://www.npmjs.com/package/pluralize#usage
   * @example <Pluralized count={3}>duck</Pluralized> // ducks
   * @example <Pluralized count={1}>duck</Pluralized> // duck
   * @example <Pluralized count={3}>goose</Pluralized> // geese
   * @example <Pluralized count={1}>goose</Pluralized> // goose
   */
  children: string;
  /**
   * Whether to prefix with the number (e.g. 3 ducks)
   *
   * @default false
   * @type {boolean}
   * @memberof IPluralizedProps
   * @see https://www.npmjs.com/package/pluralize#usage
   * @example <Pluralized count={3} inclusive>duck</Pluralized> // 3 ducks
   * @example <Pluralized count={1} inclusive>duck</Pluralized> // 1 duck
   * @example <Pluralized count={3} inclusive>goose</Pluralized> // 3 geese
   * @example <Pluralized count={1} inclusive>goose</Pluralized> // 1 goose
   */
  inclusive?: boolean;
}

export const Pluralized = memo(
  ({ count, children, inclusive }: IPluralizedProps) => {
    const text = useMemo(
      () => pluralize(children, count, inclusive),
      [children, count, inclusive]
    );
    return <>{text}</>;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.children === nextProps.children &&
      prevProps.count === nextProps.count &&
      prevProps.inclusive === nextProps.inclusive
    );
  }
);
